import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaTimes,
  FaSpinner,
  FaMapMarkerAlt,
  FaHome,
  FaSignInAlt,
  FaExternalLinkAlt,
  FaRoute,
  FaChevronDown,
  FaChevronRight,
  FaMap,
  FaFileExcel,
} from "react-icons/fa";
import toast from "react-hot-toast";
import axiosInstance from "../../../libs/axios";
import { ExcelDownload } from "../../../utils/excel.utils";
import RouteMap, { type RouteMapStop } from "./RouteMap";

/**
 * Site to Site Distance — one engineer's day, as a route.
 *
 * The Daily Travel row only carries totals. This is the day itself, drawn and
 * tabulated, in the two ways it can be measured:
 *
 *   From Attendance   attendance check-in -> each site in visit order ->
 *                     attendance check-out. When the engineer never marked a
 *                     check-out the route closes back at the check-in, which is
 *                     the only anchor that day actually has.
 *   From Home         the user's reporting location -> each site -> back to the
 *                     reporting location.
 *
 * Both routes walk the same ordered list of sites — the ones the engineer had
 * in field work that day — so the only difference is the two ends, and their
 * totals are directly comparable. The smaller of the two is the day's Minimum
 * Distance Travel, shown against whichever route is on screen so it is obvious
 * which one won.
 *
 * Each route is measured TWICE over the same stops:
 *
 *   Site       through the site's registered coordinates — where the site is.
 *   Check In   through the coordinates the engineer's phone reported when they
 *              unlocked the site — where the engineer actually was.
 *
 * Both are shown per hop along with their Difference — how far apart the two
 * measurements of that hop are — because the gap between them is the
 * interesting number: a large one means the engineer opened the site from
 * somewhere it is not.
 *
 * The route opens on From Home: the reporting location is the anchor a day is
 * claimed against, so it is the route read first.
 */

type RouteType = "attendance" | "home";

interface RouteStop {
  sequence: number;
  site_row_id: number;
  site_id: string | null;
  site_name: string | null;
  check_in_time: string | null;
  fw_start: string | null;
  survey_date: string | null;
  latitude: number | null;
  longitude: number | null;
  lat_long: string | null;
  /** Where the engineer unlocked (checked in to) the site. */
  checkin_latitude: number | null;
  checkin_longitude: number | null;
  checkin_lat_long: string | null;
  location_name: string | null;
  checkin_location_name: string | null;
  distance_from_previous_km: number | null;
  distance_from_previous_checkin_km: number | null;
  distance_difference_km: number | null;
  distance_min_km: number | null;
}

interface RouteTerminus {
  label: string;
  kind: string;
  time: string | null;
  latitude: number | null;
  longitude: number | null;
  lat_long: string | null;
  location_name: string | null;
  fell_back_to_check_in?: boolean;
  distance_from_previous_km?: number | null;
  distance_from_previous_checkin_km?: number | null;
  distance_difference_km?: number | null;
  distance_min_km?: number | null;
}

interface DayRoute {
  type: RouteType;
  label: string;
  start: RouteTerminus;
  end: RouteTerminus;
  stops: RouteStop[];
  total_distance_km: number | null;
  total_distance_checkin_km: number | null;
  total_distance_min_km: number | null;
  total_distance_difference_km: number | null;
  encoded_polyline: string | null;
  path: Array<[number, number]>;
  google_maps_url: string | null;
  route_path: string | null;
  is_routed: boolean;
  unavailable_reason: string | null;
}

interface DayRoutes {
  user_id: number;
  user_name: string | null;
  employee_code: string | null;
  work_date: string;
  site_count: number;
  minimum_distance_travel_km: number | null;
  minimum_distance_travel_checkin_km: number | null;
  minimum_distance_travel_overall_km: number | null;
  attendance: DayRoute;
  home: DayRoute;
}

export interface SiteRouteModalTarget {
  userId: number;
  /** IST calendar date, YYYY-MM-DD. */
  workDate: string;
  userName: string;
  workDateLabel: string;
}

interface SiteRouteModalProps {
  target: SiteRouteModalTarget | null;
  onClose: () => void;
}

const km = (v: number | null | undefined) =>
  v === null || v === undefined ? "NA" : `${Number(v).toFixed(2)} km`;

const timeText = (v: string | null) => {
  if (!v) return "NA";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "NA";
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Survey Date is a date, not an instant — the time of day says nothing here. */
const dateText = (v: string | null) => {
  if (!v) return "NA";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "NA";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const text = (v: string | null | undefined) => (v && String(v).trim() ? String(v) : "NA");

/**
 * A location on one line, cut off with an ellipsis, with the whole address in
 * the hover tooltip.
 *
 * Reverse-geocoded names run to a full postal address, and letting them wrap
 * pushed every row of the route table several lines tall — the distances the
 * table exists for ended up scattered down the page. One line each keeps the
 * route readable, and the full text is a hover away.
 */
const LocationText: React.FC<{ value: string | null | undefined; className?: string }> = ({
  value,
  className = "",
}) => {
  const full = text(value);
  return (
    <span className={`block truncate whitespace-nowrap ${className}`} title={full === "NA" ? undefined : full}>
      {full}
    </span>
  );
};

/** Only the sites are numbered — the two ends are S and E. */
const badgeFor = (kind: RouteMapStop["kind"], sequence: number) =>
  kind === "start" ? "S" : kind === "end" ? "E" : String(sequence);

const SiteRouteModal: React.FC<SiteRouteModalProps> = ({ target, onClose }) => {
  const [data, setData] = useState<DayRoutes | null>(null);
  const [loading, setLoading] = useState(false);
  const [routeType, setRouteType] = useState<RouteType>("home");
  // The map is the expensive half of this modal — a tile layer plus a routed
  // polyline — and most opens are read off the table. It stays folded away
  // until asked for, and stays open once opened while the modal is up.
  const [showMap, setShowMap] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    setData(null);
    try {
      const res = await axiosInstance.get("/reports/daily-travel/routes", {
        params: { user_id: target.userId, work_date: target.workDate },
      });
      if (res.data?.success) {
        setData(res.data.data as DayRoutes);
      } else {
        toast.error(res.data?.message || "Failed to load the route for this day");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to load the route for this day",
      );
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    setRouteType("home");
    setShowMap(false);
    load();
  }, [load]);

  useEffect(() => {
    if (!target) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [target, onClose]);

  const route = data ? data[routeType] : null;

  const mapStops: RouteMapStop[] = useMemo(() => {
    if (!route) return [];
    return [
      {
        badge: "S",
        title: route.start.label,
        subtitle: route.start.location_name,
        latitude: route.start.latitude,
        longitude: route.start.longitude,
        kind: "start" as const,
      },
      ...route.stops.map((stop) => ({
        badge: badgeFor("site", stop.sequence),
        title: stop.site_name || stop.site_id || `Site ${stop.sequence}`,
        subtitle: stop.location_name,
        latitude: stop.latitude,
        longitude: stop.longitude,
        kind: "site" as const,
      })),
      {
        badge: "E",
        title: route.end.label,
        subtitle: route.end.location_name,
        latitude: route.end.latitude,
        longitude: route.end.longitude,
        kind: "end" as const,
      },
    ];
  }, [route]);

  // The route on screen, as flat rows: the start, every stop, the end and a
  // total line — the same order and the same strings the table shows, so the
  // workbook cannot drift from what was exported from.
  const exportRows = useMemo(() => {
    if (!route) return [];
    const row = (
      marker: string,
      stop: string,
      siteName: string,
      time: string,
      siteKm: number | null | undefined,
      checkinKm: number | null | undefined,
      diffKm: number | null | undefined,
      latLong: string,
      checkinLatLong: string,
      locationName: string,
      fwStart: string,
      surveyDate: string,
    ) => ({
      "#": marker,
      "Visit Site ID": stop,
      "Site Name": siteName,
      "Check In Time": time,
      "Distance from Previous (Site)": km(siteKm),
      "Distance from Previous (Check In)": km(checkinKm),
      "Difference": km(diffKm),
      "Site Lat Long": latLong,
      "Check In Lat Long": checkinLatLong,
      "Location Name": locationName,
      "FW Start": fwStart,
      "Survey Date": surveyDate,
    });

    return [
      row(
        "S", route.start.label, "", timeText(route.start.time),
        null, null, null,
        text(route.start.lat_long), "", text(route.start.location_name), "", "",
      ),
      ...route.stops.map((stop) =>
        row(
          String(stop.sequence), text(stop.site_id), text(stop.site_name),
          timeText(stop.check_in_time),
          stop.distance_from_previous_km,
          stop.distance_from_previous_checkin_km,
          stop.distance_difference_km,
          text(stop.lat_long), text(stop.checkin_lat_long), text(stop.location_name),
          timeText(stop.fw_start), dateText(stop.survey_date),
        ),
      ),
      row(
        "E", route.end.label, "", timeText(route.end.time),
        route.end.distance_from_previous_km,
        route.end.distance_from_previous_checkin_km,
        route.end.distance_difference_km,
        text(route.end.lat_long), "", text(route.end.location_name), "", "",
      ),
      row(
        "", `Total — ${route.label}`, "", "",
        route.total_distance_km,
        route.total_distance_checkin_km,
        route.total_distance_difference_km,
        "", "", "", "", "",
      ),
    ];
  }, [route]);

  if (!target) return null;

  // The route whose total equals the minimum is the one the day is charged at.
  const isMinimumRoute =
    !!route &&
    !!data &&
    data.minimum_distance_travel_km !== null &&
    route.total_distance_km !== null &&
    Math.abs(route.total_distance_km - data.minimum_distance_travel_km) < 0.005;

  // Home sits first and is the landing tab: the reporting location is the
  // anchor the day is actually claimed against, so it is the one opened on.
  const TOGGLE: Array<{ value: RouteType; label: string; icon: React.ReactNode }> = [
    { value: "home", label: "From Home", icon: <FaHome className="w-3 h-3" /> },
    { value: "attendance", label: "From Attendance", icon: <FaSignInAlt className="w-3 h-3" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
        {/* Deliberately wide and tall: the route carries a map and an
            eight-column table, and the old max-w-2xl box could show neither. */}
        <div className="relative w-full max-w-7xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
          {/* ── Header ─────────────────────────────────────────────────── */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FaRoute className="w-4 h-4 text-blue-700" /> Site to Site Distance
              </h3>
              <p className="text-sm text-gray-600 mt-0.5 truncate">
                {data?.user_name || target.userName}
                {data?.employee_code ? ` · ${data.employee_code}` : ""} &middot; {target.workDateLabel}
                {data ? ` · ${data.site_count} site${data.site_count === 1 ? "" : "s"} in field work` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded flex-shrink-0"
              aria-label="Close"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>

          {/* ── Toggle + totals ────────────────────────────────────────── */}
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
              {TOGGLE.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRouteType(option.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    routeType === option.value
                      ? "bg-blue-800 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {option.icon}
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span
                className="px-3 py-1.5 rounded border border-gray-200 bg-white text-xs text-gray-600"
                title="This route measured through the sites' registered coordinates."
              >
                This route (site):{" "}
                <span className="font-semibold text-gray-900">{km(route?.total_distance_km)}</span>
              </span>
              <span
                className="px-3 py-1.5 rounded border border-gray-200 bg-white text-xs text-gray-600"
                title="The same route measured through the engineer's check-in (site unlock) positions."
              >
                This route (check in):{" "}
                <span className="font-semibold text-gray-900">
                  {km(route?.total_distance_checkin_km)}
                </span>
                {route?.total_distance_difference_km !== null &&
                route?.total_distance_difference_km !== undefined ? (
                  <span className="text-gray-500">
                    {" "}
                    · diff {km(route.total_distance_difference_km)}
                  </span>
                ) : null}
              </span>
              <span
                className="px-3 py-1.5 rounded border border-blue-200 bg-blue-50 text-xs text-blue-800"
                title="The smaller of this route's two measurements."
              >
                This route (minimum):{" "}
                <span className="font-semibold">{km(route?.total_distance_min_km)}</span>
              </span>
              <span
                className={`px-3 py-1.5 rounded border text-xs ${
                  isMinimumRoute
                    ? "border-green-300 bg-green-50 text-green-800"
                    : "border-gray-200 bg-white text-gray-600"
                }`}
                title="The smaller of the two routes — what the day is charged at."
              >
                Minimum Distance Travel:{" "}
                <span className="font-semibold">{km(data?.minimum_distance_travel_km)}</span>
                {isMinimumRoute ? " · this route" : ""}
                <span className="text-gray-500">
                  {" "}
                  · check in {km(data?.minimum_distance_travel_checkin_km)}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setShowExcelModal(true)}
                disabled={exportRows.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-800 bg-green-50 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaFileExcel className="w-3 h-3" /> Export Excel
              </button>
              {route?.google_maps_url && (
                <a
                  href={route.google_maps_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100"
                >
                  Open in Google Maps <FaExternalLinkAlt className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>

          {/* ── Body ───────────────────────────────────────────────────── */}
          <div className="max-h-[70vh] overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
                <FaSpinner className="w-4 h-4 animate-spin" /> Building the route…
              </div>
            ) : !route ? (
              <div className="py-16 text-center text-sm text-gray-500">
                Nothing to show for this day.
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-4">
                {route.unavailable_reason && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    {route.unavailable_reason}
                  </p>
                )}

                {/* Visit Start Check In Details — where the day began. */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-3 text-xs font-semibold text-gray-600">
                    Visit Start Check In Details
                  </div>
                  <div className="border border-gray-200 rounded px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Time</div>
                    <div className="text-sm text-gray-800">{timeText(route.start.time)}</div>
                  </div>
                  <div className="border border-gray-200 rounded px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Lat Long</div>
                    <div className="text-sm text-gray-800 font-mono break-all">
                      {text(route.start.lat_long)}
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Location Name</div>
                    <div className="text-sm text-gray-800">
                      <LocationText value={route.start.location_name} />
                    </div>
                  </div>
                </div>

                {/* Map — closed by default, opened on demand. */}
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowMap((open) => !open)}
                    aria-expanded={showMap}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
                  >
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <FaMap className="w-3.5 h-3.5 text-blue-700" />
                      Map
                      <span className="text-xs font-normal text-gray-500">
                        {showMap ? "" : "— click to view the route on a map"}
                      </span>
                    </span>
                    {showMap ? (
                      <FaChevronDown className="w-3 h-3 text-gray-500" />
                    ) : (
                      <FaChevronRight className="w-3 h-3 text-gray-500" />
                    )}
                  </button>
                  {showMap && (
                    <RouteMap
                      stops={mapStops}
                      encodedPolyline={route.encoded_polyline}
                      fallbackPath={route.path}
                      routeKey={`${routeType}-${data?.work_date}-${data?.user_id}`}
                      heightClass="h-80"
                    />
                  )}
                </div>

                {route.route_path && (
                  <div className="border border-gray-200 rounded px-3 py-2">
                    <div className="text-[11px] uppercase tracking-wide text-gray-400">Route Path</div>
                    <div className="text-sm text-gray-800 break-words">{route.route_path}</div>
                  </div>
                )}

                {/* ── The journey, stop by stop ─────────────────────────── */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left w-12">#</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Visit Site ID</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Site Name</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Check In Time</th>
                        <th
                          className="px-3 py-2 text-right whitespace-nowrap"
                          title="Measured through the site's registered coordinates"
                        >
                          Dist. from Prev (Site)
                        </th>
                        <th
                          className="px-3 py-2 text-right whitespace-nowrap"
                          title="Measured through the check-in (site unlock) position"
                        >
                          Dist. from Prev (Check In)
                        </th>
                        <th
                          className="px-3 py-2 text-right whitespace-nowrap"
                          title="How far apart the two measurements of this hop are — previous stop to the site's coordinates vs previous stop to where the engineer unlocked the site"
                        >
                          Difference
                        </th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Site Lat Long</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Check In Lat Long</th>
                        <th className="px-3 py-2 text-left">Location Name</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">FW Start</th>
                        <th className="px-3 py-2 text-left whitespace-nowrap">Survey Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {/* Start point */}
                      <tr className="bg-green-50/50">
                        <td className="px-3 py-2 text-xs font-semibold text-green-800">S</td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap" colSpan={2}>
                          <span className="inline-flex items-center gap-1.5">
                            <FaMapMarkerAlt className="w-3 h-3 text-green-700" />
                            {route.start.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {timeText(route.start.time)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-400">—</td>
                        <td className="px-3 py-2 text-right text-gray-400">—</td>
                        <td className="px-3 py-2 text-right text-gray-400">—</td>
                        <td className="px-3 py-2 text-gray-700 font-mono text-xs whitespace-nowrap">
                          {text(route.start.lat_long)}
                        </td>
                        <td className="px-3 py-2 text-gray-400">—</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[220px]">
                          <LocationText value={route.start.location_name} />
                        </td>
                        <td className="px-3 py-2 text-gray-400">—</td>
                        <td className="px-3 py-2 text-gray-400">—</td>
                      </tr>

                      {/* Sites, in the order they were opened */}
                      {route.stops.length === 0 ? (
                        <tr>
                          <td colSpan={12} className="px-3 py-6 text-center text-sm text-gray-500">
                            No site was in field work on this date, so the route has no stops.
                          </td>
                        </tr>
                      ) : (
                        route.stops.map((stop) => (
                          <tr key={stop.site_row_id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-xs font-semibold text-blue-800">
                              {stop.sequence}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {text(stop.site_id)}
                            </td>
                            <td className="px-3 py-2 text-gray-800">{text(stop.site_name)}</td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {timeText(stop.check_in_time)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                              {km(stop.distance_from_previous_km)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                              {km(stop.distance_from_previous_checkin_km)}
                            </td>
                            <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">
                              {km(stop.distance_difference_km)}
                            </td>
                            <td className="px-3 py-2 text-gray-700 font-mono text-xs whitespace-nowrap">
                              {text(stop.lat_long)}
                            </td>
                            <td
                              className="px-3 py-2 text-gray-700 font-mono text-xs whitespace-nowrap"
                              title={stop.checkin_location_name || undefined}
                            >
                              {text(stop.checkin_lat_long)}
                            </td>
                            <td className="px-3 py-2 text-gray-600 max-w-[220px]">
                              <LocationText value={stop.location_name} />
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {timeText(stop.fw_start)}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {dateText(stop.survey_date)}
                            </td>
                          </tr>
                        ))
                      )}

                      {/* End point */}
                      <tr className="bg-red-50/40">
                        <td className="px-3 py-2 text-xs font-semibold text-red-800">E</td>
                        <td className="px-3 py-2 text-gray-800" colSpan={2}>
                          <span className="inline-flex items-center gap-1.5">
                            <FaMapMarkerAlt className="w-3 h-3 text-red-700" />
                            {route.end.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {timeText(route.end.time)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                          {km(route.end.distance_from_previous_km)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-800 whitespace-nowrap">
                          {km(route.end.distance_from_previous_checkin_km)}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600 whitespace-nowrap">
                          {km(route.end.distance_difference_km)}
                        </td>
                        <td className="px-3 py-2 text-gray-700 font-mono text-xs whitespace-nowrap">
                          {text(route.end.lat_long)}
                        </td>
                        <td className="px-3 py-2 text-gray-400">—</td>
                        <td className="px-3 py-2 text-gray-600 max-w-[220px]">
                          <LocationText value={route.end.location_name} />
                        </td>
                        <td className="px-3 py-2 text-gray-400">—</td>
                        <td className="px-3 py-2 text-gray-400">—</td>
                      </tr>
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-sm font-semibold text-gray-700">
                          Total — {route.label}
                        </td>
                        <td className="px-3 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                          {km(route.total_distance_km)}
                        </td>
                        <td className="px-3 py-2 text-sm font-semibold text-gray-900 text-right whitespace-nowrap">
                          {km(route.total_distance_checkin_km)}
                        </td>
                        <td className="px-3 py-2 text-sm font-semibold text-gray-700 text-right whitespace-nowrap">
                          {km(route.total_distance_difference_km)}
                        </td>
                        <td colSpan={5} />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {route.end.fell_back_to_check_in && (
                  <p className="text-xs text-gray-500">
                    No attendance check-out was marked on this day, so the route closes back at the
                    check-in location.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* The workbook is the route on screen — switch the toggle to export the
          other one. Sits above this modal's own overlay via its fixed layer. */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportRows}
        filename={`site_to_site_distance_${routeType}_${data?.work_date || target.workDate}`}
        title={`Export Site to Site Distance — ${route?.label || ""}`}
        description="Download this day's route, measured from both the site and the check-in coordinates"
      />
    </div>
  );
};

export default SiteRouteModal;
